import { Dispatch, Action } from '@reduxjs/toolkit';
import { baseUrl, refreshAccessToken } from '../api/api';
import { store } from '../store/configureStore';
import { logout } from '../../features/account/accountSlice';
import authService from '../services/authService';

type EventHandler = (event: MessageEvent) => void;
type ErrorHandler = (error: Event) => void;
type ConnectionStatusHandler = (isConnected: boolean) => void;

interface EventSourceConfig {
  url: string;
  onMessage: EventHandler;
  onError?: ErrorHandler;
  onConnectionStatusChange?: ConnectionStatusHandler;
}

interface EventSourceInfo {
  source: EventSource;
  config: EventSourceConfig;
  reconnectAttempt: number;
  reconnectTimer?: NodeJS.Timeout;
  isActive: boolean;
  refreshAttempted?: boolean; // Track if refresh was attempted for this burst
  lastTokenRefresh?: number; // Track when the token was last refreshed
}

// Reconnection backoff times in milliseconds
const RECONNECT_TIMES = [
  1000, // 1 second
  5000, // 5 seconds
  10000, // 10 seconds
  20000, // 20 seconds
  60000, // 1 minute
  120000, // 2 minutes
  300000, // 5 minutes
  600000, // 10 minutes
  1200000, // 20 minutes
  3600000, // 1 hour
];

class EventSourceManager {
  private eventSources: Map<string, EventSourceInfo> = new Map();

  setupEventSource(key: string, config: EventSourceConfig) {
    if (this.eventSources.has(key)) {
      const existingInfo = this.eventSources.get(key)!;

      // If the source is already connected, don't reconnect
      if (existingInfo.source.readyState === EventSource.OPEN) {
        return;
      }

      // If there's a pending reconnect, clear it
      if (existingInfo.reconnectTimer) {
        clearTimeout(existingInfo.reconnectTimer);
      }

      // Close the existing connection
      existingInfo.source.close();
    }

    this.connectEventSource(key, config, 0);
  }

  // Manual reconnection method
  reconnectEventSource(key: string): boolean {
    const info = this.eventSources.get(key);
    if (!info) {
      console.warn(`EventSource with key ${key} not found for reconnection`);
      return false;
    }

    // If already connected, no need to reconnect
    if (info.source.readyState === EventSource.OPEN) {
      return true;
    }

    // If there's a pending reconnect, clear it
    if (info.reconnectTimer) {
      clearTimeout(info.reconnectTimer);
      info.reconnectTimer = undefined;
    }

    // Close the existing connection
    info.source.close();

    // Mark as active (in case it was inactive)
    info.isActive = true;

    // Reset reconnect attempt counter to start fresh
    info.reconnectAttempt = 0;

    // Reconnect with original config
    this.connectEventSource(key, info.config, 0);

    return true;
  }

  private async connectEventSource(
    key: string,
    config: EventSourceConfig,
    attempt: number
  ) {
    try {
      // Always use the latest token from the store
      let url = config.url;
      const user = store.getState().account.user;
      if (url.includes('token=')) {
        // Replace token param with latest
        url = url.replace(
          /token=([^&]*)/,
          `token=${encodeURIComponent(user?.accessToken || '')}`
        );
      }

      const eventSource = new EventSource(url);

      // Create info object
      const eventSourceInfo: EventSourceInfo = {
        source: eventSource,
        config: { ...config, url },
        reconnectAttempt: attempt,
        isActive: true,
        refreshAttempted: false,
        lastTokenRefresh: undefined,
      };

      // Store the event source info
      this.eventSources.set(key, eventSourceInfo);

      // Setup handlers
      eventSource.onmessage = config.onMessage;

      eventSource.onopen = () => {
        // Reset reconnect attempt and refreshAttempted on successful connection
        const info = this.eventSources.get(key);
        if (info) {
          info.reconnectAttempt = 0;
          info.refreshAttempted = false;
          if (info.config.onConnectionStatusChange) {
            info.config.onConnectionStatusChange(true);
          }
        }
      };

      eventSource.onerror = async (error) => {
        // Call the provided error handler
        if (config.onError) {
          config.onError(error);
        }

        const info = this.eventSources.get(key);
        if (!info || !info.isActive) {
          return; // Don't reconnect if the source is no longer active
        }

        // Notify about connection status change
        if (info.config.onConnectionStatusChange) {
          info.config.onConnectionStatusChange(false);
        }

        // Close the current connection
        eventSource.close();

        // Check if we should attempt a token refresh
        const now = Date.now();
        const shouldRefreshToken =
          // Only refresh if we haven't tried in the last 30 seconds
          (!info.lastTokenRefresh || now - info.lastTokenRefresh > 30000) &&
          // Only try refresh after at least one failed reconnect
          info.reconnectAttempt > 0 &&
          // Only try refresh once per reconnect burst
          !info.refreshAttempted;

        if (shouldRefreshToken) {
          info.refreshAttempted = true;
          info.lastTokenRefresh = now;
          console.log(`Attempting token refresh for EventSource ${key}...`);

          try {
            // Try to refresh the token
            const newToken = await refreshAccessToken();
            if (newToken) {
              console.log(
                `Token refreshed for EventSource ${key}, reconnecting...`
              );

              // Update the URL with the new token
              const newUrl = info.config.url.replace(
                /token=([^&]*)/,
                `token=${encodeURIComponent(newToken)}`
              );

              if (info.config.onConnectionStatusChange) {
                info.config.onConnectionStatusChange(false);
              }

              // Reset reconnect attempt counter to start fresh
              info.reconnectAttempt = 0;

              // Reset refreshAttempted for the next burst
              info.refreshAttempted = false;

              // Reconnect with the new token
              this.connectEventSource(key, { ...info.config, url: newUrl }, 0);
              return;
            } else {
              // Token refresh failed, logout
              console.error(`Token refresh failed for EventSource ${key}`);
              store.dispatch(logout());
              info.isActive = false;
              return;
            }
          } catch (refreshErr) {
            // Token refresh failed, logout
            console.error(
              `Token refresh error for EventSource ${key}:`,
              refreshErr
            );
            store.dispatch(logout());
            info.isActive = false;
            return;
          }
        }

        // Normal reconnection logic
        const nextAttempt = info.reconnectAttempt + 1;
        const delay =
          RECONNECT_TIMES[Math.min(nextAttempt, RECONNECT_TIMES.length - 1)];

        console.log(
          `EventSource ${key} disconnected, reconnecting in ${delay / 1000} seconds...`
        );

        info.reconnectTimer = setTimeout(() => {
          this.connectEventSource(key, info.config, nextAttempt);
        }, delay);
      };
    } catch (error) {
      console.error('Error creating EventSource:', error);

      const delay =
        RECONNECT_TIMES[Math.min(attempt, RECONNECT_TIMES.length - 1)];

      setTimeout(() => {
        this.connectEventSource(key, config, attempt + 1);
      }, delay);
    }
  }
  closeEventSource(key: string) {
    const info = this.eventSources.get(key);
    if (info) {
      // Mark as inactive to prevent reconnection attempts
      info.isActive = false;

      // Clear any pending reconnect timer
      if (info.reconnectTimer) {
        clearTimeout(info.reconnectTimer);
      }

      // Close the source
      info.source.close();
      this.eventSources.delete(key);
    }
  }

  closeAllEventSources() {
    this.eventSources.forEach((info) => {
      info.isActive = false;
      if (info.reconnectTimer) {
        clearTimeout(info.reconnectTimer);
      }
      info.source.close();
    });
    this.eventSources.clear();
  }

  getConnectionStatus(key: string): boolean {
    const info = this.eventSources.get(key);
    return info ? info.source.readyState === EventSource.OPEN : false;
  }
}

export const eventSourceManager = new EventSourceManager();

// Helper function to create event source setup thunk
// Always uses the latest token from the store
export function createEventSourceThunk(
  key: string,
  urlGenerator: (arg: unknown) => string,
  onMessage: (dispatch: Dispatch<Action>, event: MessageEvent) => void,
  onConnectionStatusChange?: (
    dispatch: Dispatch<Action>,
    isConnected: boolean
  ) => void
) {
  return (arg: unknown) => (dispatch: Dispatch<Action>) => {
    let token = authService.getAccessToken();
    if (!token) {
      refreshAccessToken().then(() => {
        token = authService.getAccessToken();
      });
    }

    if (!token) {
      return;
    }
    const baseUrlWithToken =
      baseUrl +
      urlGenerator(arg) +
      (urlGenerator(arg).includes('?') ? '&' : '?') +
      `token=${encodeURIComponent(token)}`;

    eventSourceManager.setupEventSource(key, {
      url: baseUrlWithToken,
      onMessage: (event) => onMessage(dispatch, event),
      onConnectionStatusChange: onConnectionStatusChange
        ? (isConnected) => onConnectionStatusChange(dispatch, isConnected)
        : undefined,
    });
  };
}

// Helper function to create event source close thunk
export function createEventSourceCloseThunk(key: string) {
  return () => () => {
    eventSourceManager.closeEventSource(key);
  };
}

// Helper function to get current connection status
export function getEventSourceConnectionStatus(key: string): boolean {
  return eventSourceManager.getConnectionStatus(key);
}

// Helper function to manually reconnect an event source
export function reconnectEventSource(key: string): boolean {
  return eventSourceManager.reconnectEventSource(key);
}
